import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, SearchX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime } from '@/lib/formatDate';
import { CharCounter } from '@/components/shared/CharCounter';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorState } from '@/components/shared/ErrorState';
import { PageHeader } from '@/components/shared/PageHeader';
import { SupportTicketStatusBadge } from '@/components/shared/SupportTicketStatusBadge';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useAdminMetric } from '@/hooks/useAdminMetric';
import { AdminApiError } from '@/services/adminApiFetch';
import {
  adminSupportService,
  ADMIN_SUPPORT_CHANGED_EVENT,
  SUPPORT_MESSAGE_BODY_MAX,
  type AdminSupportMessage,
  type AdminSupportTicket,
  type AdminSupportTicketListResponse,
  type AdminSupportTicketReason,
  type AdminSupportTicketStatus,
} from '@/services/adminSupportService';

const PAGE_SIZE = 20;
const STATUS_VALUES: readonly AdminSupportTicketStatus[] = ['open', 'in_analysis', 'closed'];
const REASON_VALUES: readonly AdminSupportTicketReason[] = [
  'question',
  'problem',
  'suggestion',
  'other',
];
// Same 300ms bound as the LeadsPage search debounce (§21.5).
const TENANT_FILTER_DEBOUNCE_MS = 300;
// Single height token shared by every filter control — mirrors LeadsPage's
// FILTER_CONTROL_CLASS (a raw <select> and a shadcn <Input> misalign by 8px
// without it).
const FILTER_CONTROL_CLASS = 'h-9';

type DetailState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; errorKey: string }
  | { status: 'ok'; ticket: AdminSupportTicket; messages: AdminSupportMessage[] };

function notifyChanged() {
  window.dispatchEvent(new Event(ADMIN_SUPPORT_CHANGED_EVENT));
}

/**
 * Maps a caught admin-plane error to a friendly, translated i18n key — NEVER
 * the raw server message (F064/F093 convention, admin-plane variant of
 * `apiErrorMessage`, needed because `adminApiFetch` throws `AdminApiError`,
 * not the tenant-plane `ApiError`).
 */
function supportErrorKey(err: unknown): string {
  if (err instanceof AdminApiError) {
    if (err.status === 409) return 'admin.support.errors.conflict';
    if (err.status === 422) return 'admin.support.errors.validation';
    if (err.status === 404) return 'admin.support.errors.not_found';
  }
  return 'common.error_generic';
}

/**
 * F162 — admin-plane cross-tenant support ticket management. Master/detail
 * on one page (no nested route): a filterable, paginated list on the left
 * (desktop) / full-width (mobile, until a ticket is selected) and the
 * selected ticket's thread + composer + status control on the right
 * (desktop) / full-width (mobile, replacing the list — a back button
 * returns). Works down to 375px per §21.7.
 */
export function AdminSupportPage() {
  const { t } = useTranslation();
  const { admin } = useAdminAuth();
  // `readonly` admins get a read-only view (backend enforces this with 403 on
  // every write endpoint regardless — this is UI politeness, not the guard).
  const canMutate = admin ? admin.role !== 'readonly' : true;

  const [statusFilter, setStatusFilter] = useState<AdminSupportTicketStatus | ''>('');
  const [reasonFilter, setReasonFilter] = useState<AdminSupportTicketReason | ''>('');
  const [tenantInput, setTenantInput] = useState('');
  const [tenantFilter, setTenantFilter] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = tenantInput.trim();
      const parsed = trimmed === '' ? NaN : Number(trimmed);
      setTenantFilter(Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
      setPage(1);
    }, TENANT_FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [tenantInput]);

  const [listState, reloadList] = useAdminMetric<AdminSupportTicketListResponse>(
    useCallback(
      () =>
        adminSupportService.list({
          status: statusFilter || undefined,
          reason: reasonFilter || undefined,
          tenant_id: tenantFilter,
          page,
          page_size: PAGE_SIZE,
        }),
      [statusFilter, reasonFilter, tenantFilter, page]
    )
  );

  const [detailState, setDetailState] = useState<DetailState>({ status: 'idle' });

  // NOTE: deliberately no dependency on `t` — react-i18next's `t` is safe to
  // omit in practice, and depending on it here previously caused an infinite
  // fetch loop in tests where the `useTranslation` mock returns a fresh `t`
  // identity every render (`fetchDetail` identity churn -> effect reruns ->
  // re-render -> new `t` -> repeat). Store the raw error KEY, not the
  // translated string, and translate at render time instead.
  const fetchDetail = useCallback(async (id: number) => {
    setDetailState({ status: 'loading' });
    try {
      const result = await adminSupportService.get(id);
      setDetailState({ status: 'ok', ticket: result.ticket, messages: result.messages });
    } catch (e) {
      setDetailState({ status: 'error', errorKey: supportErrorKey(e) });
    }
  }, []);

  useEffect(() => {
    if (selectedId !== null) void fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  // After any mutation: refresh the list, the (cross-page) nav badge, and —
  // if a ticket is open — its detail thread.
  const refreshAfterMutation = useCallback(async () => {
    reloadList();
    notifyChanged();
    if (selectedId !== null) await fetchDetail(selectedId);
  }, [reloadList, selectedId, fetchDetail]);

  const handleSelect = (id: number) => setSelectedId(id);
  const handleBack = () => setSelectedId(null);

  const clearFilters = () => {
    setStatusFilter('');
    setReasonFilter('');
    setTenantInput('');
    setTenantFilter(undefined);
    setPage(1);
  };

  const items = listState.status === 'ok' ? listState.data.items : null;
  const total = listState.status === 'ok' ? listState.data.total : 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasActiveFilters = Boolean(statusFilter) || Boolean(reasonFilter) || Boolean(tenantFilter);

  function renderList() {
    if (listState.status === 'loading') {
      return (
        <div className="flex flex-col gap-2" data-testid="admin-support-skeleton">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    }

    if (listState.status === 'error') {
      return (
        <ErrorState
          variant="load-failed"
          title={t('admin.support.list.load_error_title')}
          body={t('admin.support.list.load_error_body')}
          cta={
            <Button type="button" variant="outline" onClick={reloadList}>
              {t('admin.support.retry')}
            </Button>
          }
        />
      );
    }

    if (!items || items.length === 0) {
      return (
        <EmptyState
          icon={hasActiveFilters ? SearchX : Inbox}
          title={t('admin.support.list.empty_title')}
          subtitle={t('admin.support.list.empty_subtitle')}
          cta={
            hasActiveFilters ? (
              <Button
                type="button"
                variant="outline"
                data-testid="admin-support-clear-filters"
                onClick={clearFilters}
              >
                {t('leads.empty_search.cta')}
              </Button>
            ) : undefined
          }
        />
      );
    }

    return (
      <>
        {/* Desktop table — §21.7 pattern: hidden on mobile, replaced by cards below. */}
        <div data-testid="admin-support-desktop-table" className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.support.table.tenant')}</TableHead>
                <TableHead>{t('admin.support.table.reason')}</TableHead>
                <TableHead>{t('admin.support.table.status')}</TableHead>
                <TableHead>{t('admin.support.table.created')}</TableHead>
                <TableHead className="text-right">{t('admin.support.table.messages')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((ticket) => (
                <TableRow
                  key={ticket.id}
                  data-testid={`admin-support-row-${ticket.id}`}
                  role="button"
                  tabIndex={0}
                  aria-label={ticket.tenant_business_name}
                  className={cn('cursor-pointer', selectedId === ticket.id && 'bg-muted')}
                  onClick={() => handleSelect(ticket.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelect(ticket.id);
                    }
                  }}
                >
                  <TableCell className="max-w-56 min-w-0 truncate font-medium">
                    {ticket.tenant_business_name}
                  </TableCell>
                  <TableCell>{t(`support.reason.${ticket.reason}`)}</TableCell>
                  <TableCell>
                    <SupportTicketStatusBadge status={ticket.status} />
                  </TableCell>
                  <TableCell>{formatDate(ticket.created_at)}</TableCell>
                  <TableCell className="text-right tabular-nums">{ticket.message_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards — §21.7 pattern. */}
        <div data-testid="admin-support-mobile-cards" className="flex flex-col gap-3 md:hidden">
          {items.map((ticket) => (
            <button
              type="button"
              key={ticket.id}
              data-testid={`admin-support-card-${ticket.id}`}
              onClick={() => handleSelect(ticket.id)}
              className="rounded-lg border p-4 text-left transition-colors hover:bg-muted"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0 truncate font-medium">{ticket.tenant_business_name}</span>
                <SupportTicketStatusBadge status={ticket.status} className="shrink-0" />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{t(`support.reason.${ticket.reason}`)}</span>
                <span>{formatDate(ticket.created_at)}</span>
              </div>
            </button>
          ))}
        </div>

        <div
          className="flex items-center justify-between text-sm text-muted-foreground"
          data-testid="admin-support-pagination"
        >
          <span>{t('admin.support.pagination.page', { page, total: totalPages })}</span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="min-h-11 md:min-h-0"
            >
              {t('admin.support.pagination.previous')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="min-h-11 md:min-h-0"
            >
              {t('admin.support.pagination.next')}
            </Button>
          </div>
        </div>
      </>
    );
  }

  function renderDetail() {
    if (selectedId === null) {
      return (
        <div className="flex items-center justify-center rounded-lg border border-dashed p-12 text-center text-sm text-muted-foreground">
          {t('admin.support.detail.select_prompt')}
        </div>
      );
    }

    if (detailState.status === 'loading' || detailState.status === 'idle') {
      return (
        <div className="flex flex-col gap-3" data-testid="admin-support-detail-skeleton">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-40 w-full" />
        </div>
      );
    }

    if (detailState.status === 'error') {
      return (
        <ErrorState
          variant="load-failed"
          title={t('admin.support.detail.load_error_title')}
          body={t('admin.support.detail.load_error_body')}
          cta={
            <>
              <Button type="button" variant="outline" onClick={() => void fetchDetail(selectedId)}>
                {t('admin.support.retry')}
              </Button>
              <Button type="button" variant="ghost" onClick={handleBack}>
                {t('admin.support.detail.back_to_list')}
              </Button>
            </>
          }
        />
      );
    }

    return (
      <TicketDetail
        ticket={detailState.ticket}
        messages={detailState.messages}
        canMutate={canMutate}
        onBack={handleBack}
        onMutated={refreshAfterMutation}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('admin.support.title')} subtitle={t('admin.support.subtitle')} />

      {/* Filter row */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <select
          data-testid="admin-support-status-filter"
          aria-label={t('admin.support.filters.status_label')}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as AdminSupportTicketStatus | '');
            setPage(1);
          }}
          className={cn(FILTER_CONTROL_CLASS, 'rounded-lg border bg-background px-3 text-sm')}
        >
          <option value="">{t('admin.support.filters.status_all')}</option>
          {STATUS_VALUES.map((s) => (
            <option key={s} value={s}>
              {t(`support.status.${s}`)}
            </option>
          ))}
        </select>

        <select
          data-testid="admin-support-reason-filter"
          aria-label={t('admin.support.filters.reason_label')}
          value={reasonFilter}
          onChange={(e) => {
            setReasonFilter(e.target.value as AdminSupportTicketReason | '');
            setPage(1);
          }}
          className={cn(FILTER_CONTROL_CLASS, 'rounded-lg border bg-background px-3 text-sm')}
        >
          <option value="">{t('admin.support.filters.reason_all')}</option>
          {REASON_VALUES.map((r) => (
            <option key={r} value={r}>
              {t(`support.reason.${r}`)}
            </option>
          ))}
        </select>

        <Input
          data-testid="admin-support-tenant-filter"
          placeholder={t('admin.support.filters.tenant_placeholder')}
          aria-label={t('admin.support.filters.tenant_label')}
          value={tenantInput}
          onChange={(e) => setTenantInput(e.target.value)}
          className={cn(FILTER_CONTROL_CLASS, 'md:max-w-[160px]')}
          inputMode="numeric"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,380px)_1fr]">
        {/* List panel — hidden on mobile once a ticket is selected (the
            detail pane takes over full-width); always visible at md+. */}
        <div className={cn('flex flex-col gap-4', selectedId !== null && 'hidden md:flex')}>
          {renderList()}
        </div>

        {/* Detail panel — only rendered/visible on mobile once selected;
            always visible at md+ (shows a "select a ticket" prompt when
            nothing is selected). */}
        <div className={cn('flex flex-col gap-4', selectedId === null && 'hidden md:flex')}>
          {renderDetail()}
        </div>
      </div>
    </div>
  );
}

// ─── Ticket detail (thread + composer + status control) ────────────────────

interface TicketDetailProps {
  ticket: AdminSupportTicket;
  messages: AdminSupportMessage[];
  canMutate: boolean;
  onBack: () => void;
  onMutated: () => void | Promise<void>;
}

function TicketDetail({ ticket, messages, canMutate, onBack, onMutated }: TicketDetailProps) {
  const { t } = useTranslation();
  const [composerBody, setComposerBody] = useState('');
  const [composerBusy, setComposerBusy] = useState<'draft' | 'send' | null>(null);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState('');
  const [messageBusyId, setMessageBusyId] = useState<number | null>(null);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  // A freshly-selected ticket must never keep stale composer/edit state from
  // the previously viewed one.
  useEffect(() => {
    setComposerBody('');
    setComposerError(null);
    setEditingId(null);
    setEditBody('');
    setMessageError(null);
    setStatusError(null);
  }, [ticket.id]);

  const isClosed = ticket.status === 'closed';
  const composerValid =
    composerBody.trim().length > 0 && composerBody.length <= SUPPORT_MESSAGE_BODY_MAX;

  async function submitComposer(send: boolean) {
    if (!composerValid) {
      setComposerError(t('admin.support.errors.validation'));
      return;
    }
    setComposerError(null);
    setComposerBusy(send ? 'send' : 'draft');
    try {
      await adminSupportService.createMessage(ticket.id, { body: composerBody, send });
      setComposerBody('');
      await onMutated();
    } catch (e) {
      setComposerError(t(supportErrorKey(e)));
    } finally {
      setComposerBusy(null);
    }
  }

  function startEdit(message: AdminSupportMessage) {
    setEditingId(message.id);
    setEditBody(message.body);
    setMessageError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditBody('');
  }

  async function saveEdit(messageId: number) {
    const trimmed = editBody.trim();
    if (trimmed.length === 0 || editBody.length > SUPPORT_MESSAGE_BODY_MAX) {
      setMessageError(t('admin.support.errors.validation'));
      return;
    }
    setMessageBusyId(messageId);
    setMessageError(null);
    try {
      await adminSupportService.updateMessage(messageId, editBody);
      setEditingId(null);
      await onMutated();
    } catch (e) {
      setMessageError(t(supportErrorKey(e)));
    } finally {
      setMessageBusyId(null);
    }
  }

  async function sendDraft(messageId: number) {
    setMessageBusyId(messageId);
    setMessageError(null);
    try {
      await adminSupportService.sendMessage(messageId);
      await onMutated();
    } catch (e) {
      setMessageError(t(supportErrorKey(e)));
    } finally {
      setMessageBusyId(null);
    }
  }

  async function moveToInAnalysis() {
    setStatusBusy(true);
    setStatusError(null);
    try {
      await adminSupportService.updateStatus(ticket.id, 'in_analysis');
      await onMutated();
    } catch (e) {
      setStatusError(t(supportErrorKey(e)));
    } finally {
      setStatusBusy(false);
    }
  }

  async function closeTicket() {
    setStatusBusy(true);
    setStatusError(null);
    try {
      await adminSupportService.updateStatus(ticket.id, 'closed');
      await onMutated();
    } catch (e) {
      setStatusError(t(supportErrorKey(e)));
    } finally {
      setStatusBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="admin-support-detail">
      <div className="flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-fit min-h-11 md:hidden"
            data-testid="admin-support-back-button"
            onClick={onBack}
          >
            {t('admin.support.detail.back_to_list')}
          </Button>
          <h2 className="min-w-0 truncate text-lg font-semibold">{ticket.tenant_business_name}</h2>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <SupportTicketStatusBadge
              status={ticket.status}
              data-testid="admin-support-detail-status-badge"
            />
            <span>{t(`support.reason.${ticket.reason}`)}</span>
            <span aria-hidden="true">·</span>
            <span>{formatDate(ticket.created_at)}</span>
          </div>
        </div>

        {canMutate ? (
          <div className="flex shrink-0 flex-wrap gap-2">
            {ticket.status === 'open' ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11 sm:min-h-0"
                data-testid="admin-support-status-in-analysis"
                disabled={statusBusy}
                onClick={() => void moveToInAnalysis()}
              >
                {t('admin.support.status_action.move_to_in_analysis')}
              </Button>
            ) : null}
            {!isClosed ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11 sm:min-h-0"
                data-testid="admin-support-status-close"
                disabled={statusBusy}
                onClick={() => setCloseConfirmOpen(true)}
              >
                {t('admin.support.status_action.close')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {statusError ? (
        <p role="alert" className="text-sm text-destructive">
          {statusError}
        </p>
      ) : null}

      {isClosed ? (
        <p
          className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground"
          data-testid="admin-support-closed-notice"
        >
          {t('admin.support.status_action.closed_notice')}
        </p>
      ) : null}

      {!canMutate ? (
        <p
          className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground"
          data-testid="admin-support-readonly-notice"
        >
          {t('admin.support.detail.readonly_notice')}
        </p>
      ) : null}

      {/* Thread — the ticket's description is the first bubble, straight
          from the ticket row (F162 note: never mirrored as a message). */}
      <div className="flex flex-col gap-3" data-testid="admin-support-thread">
        <div
          className="flex flex-col gap-1 rounded-lg border bg-muted px-3.5 py-2.5"
          data-testid="admin-support-bubble-description"
        >
          <span className="text-xs font-medium text-muted-foreground">
            {t('admin.support.detail.description_label')}
          </span>
          <p className="whitespace-pre-wrap break-words text-sm">{ticket.description}</p>
        </div>

        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('admin.support.detail.no_replies')}</p>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              editing={editingId === message.id}
              editBody={editBody}
              onEditBodyChange={setEditBody}
              busy={messageBusyId === message.id}
              canMutate={canMutate}
              onStartEdit={() => startEdit(message)}
              onCancelEdit={cancelEdit}
              onSaveEdit={() => void saveEdit(message.id)}
              onSend={() => void sendDraft(message.id)}
            />
          ))
        )}
      </div>

      {messageError ? (
        <p role="alert" className="text-sm text-destructive">
          {messageError}
        </p>
      ) : null}

      {/* Composer — hidden once closed (terminal) or for readonly admins. */}
      {canMutate && !isClosed ? (
        <div className="flex flex-col gap-2 border-t pt-4" data-testid="admin-support-composer">
          <label htmlFor="admin-support-composer-textarea" className="text-sm font-medium">
            {t('admin.support.composer.label')}
          </label>
          <Textarea
            id="admin-support-composer-textarea"
            data-testid="admin-support-composer-textarea"
            placeholder={t('admin.support.composer.placeholder')}
            value={composerBody}
            maxLength={SUPPORT_MESSAGE_BODY_MAX}
            rows={4}
            onChange={(e) => setComposerBody(e.target.value)}
          />
          <CharCounter value={composerBody} max={SUPPORT_MESSAGE_BODY_MAX} mode="always" />
          {composerError ? (
            <p role="alert" className="text-sm text-destructive">
              {composerError}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              data-testid="admin-support-save-draft"
              disabled={composerBusy !== null || !composerValid}
              onClick={() => void submitComposer(false)}
            >
              {composerBusy === 'draft'
                ? t('admin.support.composer.saving')
                : t('admin.support.composer.save_draft')}
            </Button>
            <Button
              type="button"
              className="min-h-11"
              data-testid="admin-support-send"
              disabled={composerBusy !== null || !composerValid}
              onClick={() => void submitComposer(true)}
            >
              {composerBusy === 'send'
                ? t('admin.support.composer.sending')
                : t('admin.support.composer.send')}
            </Button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={closeConfirmOpen}
        onOpenChange={setCloseConfirmOpen}
        title={t('admin.support.status_action.close_confirm_title')}
        description={t('admin.support.status_action.close_confirm_body')}
        confirmLabel={t('admin.support.status_action.close')}
        onConfirm={closeTicket}
      />
    </div>
  );
}

// ─── Message bubble (draft/sent, admin/ai_agent-aware) ──────────────────────

interface MessageBubbleProps {
  message: AdminSupportMessage;
  editing: boolean;
  editBody: string;
  onEditBodyChange: (value: string) => void;
  busy: boolean;
  canMutate: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onSend: () => void;
}

function MessageBubble({
  message,
  editing,
  editBody,
  onEditBodyChange,
  busy,
  canMutate,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onSend,
}: MessageBubbleProps) {
  const { t } = useTranslation();
  const isDraft = message.state === 'draft';
  const authorLabel = t(`admin.support.author.${message.author_type}`);

  return (
    <div
      data-testid={`admin-support-message-${message.id}`}
      data-state={message.state}
      data-author-type={message.author_type}
      className={cn(
        'flex flex-col gap-1.5 rounded-lg border px-3.5 py-2.5 sm:ml-8',
        isDraft ? 'border-dashed bg-background' : 'bg-card'
      )}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{authorLabel}</span>
        <span aria-hidden="true">·</span>
        <span data-testid={`admin-support-message-state-${message.id}`}>
          {t(`admin.support.message_state.${message.state}`)}
        </span>
        {!isDraft && message.sent_at ? (
          <>
            <span aria-hidden="true">·</span>
            <span>{formatDateTime(message.sent_at)}</span>
          </>
        ) : null}
      </div>

      {editing ? (
        <div className="flex flex-col gap-2">
          <Textarea
            data-testid={`admin-support-message-edit-textarea-${message.id}`}
            value={editBody}
            maxLength={SUPPORT_MESSAGE_BODY_MAX}
            rows={3}
            onChange={(e) => onEditBodyChange(e.target.value)}
          />
          <CharCounter value={editBody} max={SUPPORT_MESSAGE_BODY_MAX} mode="always" />
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              className="min-h-11 sm:min-h-0"
              disabled={busy}
              data-testid={`admin-support-message-save-${message.id}`}
              onClick={onSaveEdit}
            >
              {t('admin.support.message_actions.save')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="min-h-11 sm:min-h-0"
              disabled={busy}
              onClick={onCancelEdit}
            >
              {t('admin.support.message_actions.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
          {isDraft && canMutate ? (
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="min-h-11 sm:min-h-0"
                disabled={busy}
                data-testid={`admin-support-message-edit-${message.id}`}
                onClick={onStartEdit}
              >
                {t('admin.support.message_actions.edit')}
              </Button>
              <Button
                type="button"
                size="sm"
                className="min-h-11 sm:min-h-0"
                disabled={busy}
                data-testid={`admin-support-message-send-${message.id}`}
                onClick={onSend}
              >
                {t('admin.support.message_actions.send')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
